// src/pages/freemium/SnapshotPage_free.tsx
// Freemium Snapshot (Home) page with Tools/Documents/Messages filters
// UPDATED: Removed DevToggle (now controlled by HomePage)

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Database, Lock } from "lucide-react";
import { FreemiumCard } from "../../components/FreemiumCard";
import { FreemiumJourneyBarV8 } from "../../components/FreemiumJourneyBarV8";
import { PersonalizeModal } from "../../components/PersonalizeModal";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumSnapshotToolsGrid } from "../../components/snapshot/FreemiumSnapshotToolsGrid";
import { mockFreemiumSnapshotCards, mockPersonalizationData } from "../../mockData/snapshotFreemiumMock";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import FreemiumJourneyHeader from "../../components/freemium/FreemiumJourneyHeader";

export default function SnapshotPage_free() {
  const navigate = useNavigate();
  const { activeFilter } = useFilter();
  const { setPageContext } = usePageContext();

  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [personalizationData, setPersonalizationData] = useState(mockPersonalizationData);

  // Set page context - empty breadcrumbs to hide global header tabs
  useEffect(() => {
    setPageContext({
      breadcrumbs: [], // Empty to hide tabs in global header (moved to page-level)
      tabs: ["Tools", "Documents", "Messages"]
    });
  }, [setPageContext]);

  // Handle freemium step navigation
  const handleFreemiumStepClick = (stepId: number) => {
    switch(stepId) {
      case 1:
        navigate('/home');
        break;
      case 2:
        navigate('/personalize');
        break;
      case 3:
        navigate('/freemium/strategy');
        break;
      case 4:
        navigate('/freemium/upgrade');
        break;
      default:
        navigate('/home');
    }
  };

  // Handle personalization completion
  const handlePersonalizationComplete = (data: any) => {
    setPersonalizationData(data);
    setShowPersonalizeModal(false);
  };

  console.log('🏠 FreemiumSnapshotPage - activeFilter:', activeFilter);

  // DOCUMENTS CONTENT (4 cards) - keeping existing structure
  const documentsCards = [
    {
      icon: <Database className="w-6 h-6" />,
      title: "Getting Started with DVA",
      description: "Quick guide to navigating your Data Value Accelerator workspace.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Understanding your personalized insights",
        "Reading the snapshot dashboard",
        "Exploring data strategy recommendations",
        "Next steps for growth"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "2.1 MB"
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Strategy Primer",
      description: "Essential concepts for building a strong data foundation.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Key data strategy components",
        "Common pitfalls to avoid",
        "Building stakeholder alignment",
        "Measuring success"
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "3.4 MB"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Advanced Analytics Playbook",
      description: "Deep dive into analytics capabilities and use case prioritization.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "ROI modeling frameworks",
        "Implementation roadmaps",
        "Resource planning guides",
        "Change management strategies"
      ],
      isPremium: true,
      fileType: "PDF",
      fileSize: "5.8 MB"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Executive Reporting Templates",
      description: "Professional templates for presenting insights to leadership.",
      contentType: 'list',
      category: 'Documents',
      listItems: [
        "Board presentation decks",
        "Executive summary formats",
        "KPI dashboards",
        "Business case templates"
      ],
      isPremium: true,
      fileType: "PDF",
      fileSize: "4.2 MB"
    }
  ];

  return (
    <>
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        
        {/* Step Header with Tabs */}
        <FreemiumJourneyHeader
          stepNumber={1}
          stepName="Snapshot"
          description="Visualize and prioritize key industry insights for your organization."
        />

        {/* Journey Bar - Always Freemium */}
        <div className="mb-6">
          <FreemiumJourneyBarV8
            currentStep={1}
            isPersonalized={personalizationData.strategy > 0}
            personalizationData={personalizationData}
            onPersonalizationClick={() => setShowPersonalizeModal(true)}
            onStepClick={handleFreemiumStepClick}
          />
        </div>

        {/* Conditional content based on activeFilter */}
        {activeFilter === 'Messages' ? (
          <MessagesContent />
        ) : activeFilter === 'Documents' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {documentsCards.map((card, idx) => (
              <FreemiumCard
                key={idx}
                icon={card.icon}
                title={card.title}
                description={card.description}
                contentType={card.contentType}
                listItems={card.listItems}
                isPremium={card.isPremium}
              />
            ))}
          </div>
        ) : (
          /* Tools view - Default */
          <div className="space-y-6">
            {/* Snapshot Tools Grid */}
            <FreemiumSnapshotToolsGrid cards={mockFreemiumSnapshotCards} />
          </div>
        )}
      </main>

      {/* Personalize Modal */}
      <PersonalizeModal
        isOpen={showPersonalizeModal}
        onClose={() => setShowPersonalizeModal(false)}
        onComplete={handlePersonalizationComplete}
        initialData={personalizationData}
      />
    </>
  );
}